import { useMemo } from "react";
import { PricingType } from "../../graphql/__generated_types__/globalTypes";
import { FindMatterQuery } from "~/gql/graphql";
import { PricingLevel, PricingOptions } from "@design-system";

export function usePricingSummary(
  matter:
    | Pick<
        FindMatterQuery["findMatter"],
        "pricingLevel" | "pricingType" | "priceOptions" | "phases"
      >
    | undefined
): PricingOptions {
  const { pricingLevel, pricingType, priceOptions: priceOptionsRaw, phases } = matter || {};

  return useMemo(() => {
    if (!pricingLevel || !pricingType || !priceOptionsRaw || !phases) {
      return {} as PricingOptions;
    }

    let pricingOptions = priceOptionsRaw;
    if (pricingLevel === PricingLevel.FeeEarner && pricingType === PricingType.EstimatedRange) {
      const totalWorkCost = { lower: 0, upper: 0 };
      for (const p of phases) {
        if (p?.work && Array.isArray(p.work)) {
          for (const w of p.work) {
            totalWorkCost.lower += w.price.lower || 0;
            totalWorkCost.upper += w.price.upper || 0;
          }
        }
      }
      pricingOptions = {
        ...priceOptionsRaw,
        estimatedRangeChosenLower: pricingOptions?.estimatedRangeChosenLower || 0,
        estimatedRangeChosenUpper: pricingOptions?.estimatedRangeChosenUpper || 0,
        estimatedRangeGpm: pricingOptions?.estimatedRangeGpm || 0,
        estimatedRangeSuggestedLower: totalWorkCost.lower,
        estimatedRangeSuggestedUpper: totalWorkCost.upper,
      };
    } else if (pricingLevel === PricingLevel.Phase && pricingType === PricingType.EstimatedRange) {
      const totalPhaseCosts = { lower: 0, upper: 0 };
      for (const p of phases) {
        totalPhaseCosts.lower += p?.priceOptions?.estimatedRangeChosenLower ?? 0;
        totalPhaseCosts.upper += p?.priceOptions?.estimatedRangeChosenUpper ?? 0;
      }
      pricingOptions = {
        ...priceOptionsRaw,
        estimatedRangeChosenLower: pricingOptions.estimatedRangeChosenLower,
        estimatedRangeChosenUpper: pricingOptions.estimatedRangeChosenUpper,
        estimatedRangeGpm: pricingOptions.estimatedRangeGpm,
        estimatedRangeSuggestedLower: totalPhaseCosts.lower,
        estimatedRangeSuggestedUpper: totalPhaseCosts.upper,
      };
    } else if (pricingLevel === PricingLevel.Phase) {
      let totalPhaseCosts = 0;
      for (const p of phases) {
        const priceOption =
          pricingType === PricingType.Capped
            ? (p?.priceOptions?.cappedSuggested as number) ?? 0
            : pricingType === PricingType.Fixed
            ? (p?.priceOptions?.fixedSuggested as number) ?? 0
            : pricingType === PricingType.Estimated
            ? (p?.priceOptions?.estimatedSuggested as number) ?? 0
            : 0;
        totalPhaseCosts += priceOption || 0;
      }
      pricingOptions = {
        ...pricingOptions,
        ...(pricingType === PricingType.Capped && {
          cappedSuggested: totalPhaseCosts,
        }),
        ...(pricingType === PricingType.Estimated && {
          estimatedSuggested: totalPhaseCosts,
        }),
        ...(pricingType === PricingType.Fixed && {
          fixedSuggested: totalPhaseCosts,
        }),
      };
    }
    return pricingOptions;
  }, [pricingLevel, pricingType, priceOptionsRaw]);
}
